import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { canDo } from "@shared/roles";
import { useLocation } from "wouter";
import { useDevisCart } from "@/contexts/DevisCartContext";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from "@/hooks/use-inventory";
import { useAuth } from "@/hooks/use-auth";
import { authFetch } from "@/lib/authFetch";
import { queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Search, Package, Pencil, Trash2, Eye, Upload, X, Check, LayoutGrid, LayoutList, FileSpreadsheet, AlertCircle, CheckCircle2, Star, ArrowUpDown, ImageIcon, MoreHorizontal, Scan, ShoppingCart, Minus, FileText, History, ChevronDown, ChevronUp } from "lucide-react";
import ProductScanner from "@/components/ProductScanner";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import type { Product, InsertProduct, ProductSku } from "@shared/schema";
import * as XLSX from "xlsx";

const typeLabels: Record<string, string> = {
  piece_detachee: "Pièce détachée",
  consommable: "Consommable",
  machine: "Machine",
  accessoire: "Accessoire",
  iot: "IoT",
  robot: "Robot",
  service: "Service",
};

const categorieRobotColors: Record<string, string> = {
  Service: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  Nettoyage: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  Industriel: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

const COMPATIBLE_ROBOTS = [
  "CC1", "CC1 2025", "CC1 Pro",
  "BellaBot", "BellaBot Pro",
  "SwiftBot", "HolaBot",
  "FlashBot", "FlashBot 2025", "FlashBot Max", "FlashBot Ultra",
  "T300", "T600",
  "KettyBot", "KettyBot Pro",
  "PuduBot", "PuduBot 2",
  "MT1", "MT1 VAC", "MT1 MAX",
  "SH1",
];

// Sorted longest-first so "FlashBot Ultra" is matched before "FlashBot" etc.
const ROBOTS_SORTED_BY_LENGTH = [...COMPATIBLE_ROBOTS].sort((a, b) => b.length - a.length);

/**
 * Parses a concatenated robot string like "CC1 CC1 2025 CC1 Pro" by scanning
 * left-to-right and greedily matching known robot names (longest first, case-insensitive).
 */
function parseRobotsCompatibles(raw: string): string[] {
  const found: string[] = [];
  let s = raw;
  while (s.length > 0) {
    let matched = false;
    for (const robot of ROBOTS_SORTED_BY_LENGTH) {
      const idx = s.toLowerCase().indexOf(robot.toLowerCase());
      if (idx !== -1) {
        if (!found.includes(robot)) found.push(robot);
        s = (s.slice(0, idx) + s.slice(idx + robot.length)).trim();
        matched = true;
        break;
      }
    }
    if (!matched) break; // nothing left to match
  }
  return found;
}

type FormData = {
  nomFr: string;
  nomEn: string;
  sku: string;
  type: string;
  descriptionFr: string;
  descriptionEn: string;
  prixHt: string;
  prixDistribution: string;
  prixRevendeurPremium: string;
  prixRevendeur: string;
  prixClientFinal: string;
  poidsNet: string;
  poidsBrut: string;
  longueur: string;
  largeur: string;
  garantie: boolean;
  favori: boolean;
  actif: boolean;
  noteInterne: string;
  robotsCompatibles: string[];
  imageUrl: string;
  financement24m: string;
  financement36m: string;
  financement48m: string;
  maintenanceMensuelle: string;
  forfaitDeploiement: string;
  locationJourPartenaire: string;
  locationSemainePartenaire: string;
  locationMoisPartenaire: string;
  locationJourClient: string;
  locationSemaineClient: string;
  locationMoisClient: string;
  locationDisponible: boolean;
  locationDelaiInstallation: string;
  features: string[];
};

const emptyForm: FormData = {
  nomFr: "",
  nomEn: "",
  sku: "",
  type: "",
  descriptionFr: "",
  descriptionEn: "",
  prixHt: "",
  prixDistribution: "",
  prixRevendeurPremium: "",
  prixRevendeur: "",
  prixClientFinal: "",
  poidsNet: "",
  poidsBrut: "",
  longueur: "",
  largeur: "",
  garantie: false,
  favori: false,
  actif: true,
  noteInterne: "",
  robotsCompatibles: [],
  imageUrl: "",
  financement24m: "",
  financement36m: "",
  financement48m: "",
  maintenanceMensuelle: "",
  forfaitDeploiement: "",
  locationJourPartenaire: "",
  locationSemainePartenaire: "",
  locationMoisPartenaire: "",
  locationJourClient: "",
  locationSemaineClient: "",
  locationMoisClient: "",
  locationDisponible: false,
  locationDelaiInstallation: "",
  features: [],
};

export default function ProductList() {
  const { profile } = useAuth();
  const { data: productsList, isLoading } = useProducts();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [robotFilter, setRobotFilter] = useState("all");
  const [favoriSort, setFavoriSort] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "grid">(() => {
    return (localStorage.getItem("products-view-mode") as "list" | "grid") || "list";
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Product | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<Product | null>(null);
  const { toast } = useToast();
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();

  const canEdit = canDo(profile?.accessRole) || profile?.role === 'technicien';
  const canCreate = canDo(profile?.accessRole);
  const canScan = canDo(profile?.accessRole) || profile?.role === 'technicien';
  const [scannerOpen, setScannerOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const { items: cartItems, addToCart } = useDevisCart();
  const [, navigate] = useLocation();

  const toggleView = (mode: "list" | "grid") => {
    setViewMode(mode);
    localStorage.setItem("products-view-mode", mode);
  };

  const availableRobots = useMemo(() => {
    if (!productsList) return [];
    const set = new Set<string>();
    productsList.forEach((p: Product) => {
      if (p.robotsCompatibles) p.robotsCompatibles.forEach((r: string) => set.add(r));
    });
    return Array.from(set).sort();
  }, [productsList]);

  const [formData, setFormData] = useState<FormData>({ ...emptyForm });
  const [editData, setEditData] = useState<FormData>({ ...emptyForm });

  const filteredProducts = productsList?.filter((item: Product) => {
    const s = search.toLowerCase().trim();
    const matchSearch = !s || (
      (item.nomFr ?? "").toLowerCase().includes(s) ||
      (item.sku ?? "").toLowerCase().includes(s) ||
      (item.descriptionFr ?? "").toLowerCase().includes(s) ||
      (item.nomEn ?? "").toLowerCase().includes(s)
    );
    const matchType = typeFilter === "all" || item.type === typeFilter;
    const matchActive = activeFilter === "all" ||
      (activeFilter === "actif" && item.actif === true) ||
      (activeFilter === "inactif" && item.actif === false);
    const matchRobot = robotFilter === "all" ||
      (Array.isArray(item.robotsCompatibles)
        ? item.robotsCompatibles.includes(robotFilter)
        : item.robotsCompatibles === robotFilter);
    return matchSearch && matchType && matchActive && matchRobot;
  })?.sort((a: Product, b: Product) => {
    if (!favoriSort) return 0;
    return (b.favori ? 1 : 0) - (a.favori ? 1 : 0);
  });

  const hasActiveFilters = search !== "" || typeFilter !== "all" || activeFilter !== "all" || robotFilter !== "all" || favoriSort;

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setActiveFilter("all");
    setRobotFilter("all");
    setFavoriSort(false);
  };

  const buildPayload = (data: FormData): InsertProduct => ({
    nomFr: data.nomFr,
    nomEn: data.nomEn || null,
    sku: data.sku,
    type: data.type as any,
    descriptionFr: data.descriptionFr || null,
    descriptionEn: data.descriptionEn || null,
    prixHt: data.prixHt || null,
    prixDistribution: data.prixDistribution || null,
    prixRevendeurPremium: data.prixRevendeurPremium || null,
    prixRevendeur: data.prixRevendeur || null,
    prixClientFinal: data.prixClientFinal || null,
    poidsNet: data.poidsNet ? parseInt(data.poidsNet) : null,
    poidsBrut: data.poidsBrut ? parseInt(data.poidsBrut) : null,
    longueur: data.longueur ? parseInt(data.longueur) : null,
    largeur: data.largeur ? parseInt(data.largeur) : null,
    garantie: data.garantie,
    favori: data.favori,
    actif: data.actif,
    noteInterne: data.noteInterne || null,
    robotsCompatibles: data.robotsCompatibles.length > 0 ? data.robotsCompatibles : null,
    imageUrl: data.imageUrl || null,
    financement24m: data.financement24m || null,
    financement36m: data.financement36m || null,
    financement48m: data.financement48m || null,
    maintenanceMensuelle: data.maintenanceMensuelle || null,
    forfaitDeploiement: data.forfaitDeploiement || null,
    locationJourPartenaire: data.locationJourPartenaire || null,
    locationSemainePartenaire: data.locationSemainePartenaire || null,
    locationMoisPartenaire: data.locationMoisPartenaire || null,
    locationJourClient: data.locationJourClient || null,
    locationSemaineClient: data.locationSemaineClient || null,
    locationMoisClient: data.locationMoisClient || null,
    locationDisponible: data.locationDisponible,
    locationDelaiInstallation: data.locationDelaiInstallation || null,
    features: data.features.length > 0 ? data.features : null,
  } as any);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync(buildPayload(formData));
      setCreateOpen(false);
      toast({ title: "Produit ajouté", description: "Le produit a été créé avec succès" });
      setFormData({ ...emptyForm });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const openEdit = (item: Product) => {
    setSelectedItem(item);
    setEditData({
      nomFr: item.nomFr,
      nomEn: item.nomEn || "",
      sku: item.sku,
      type: item.type,
      descriptionFr: item.descriptionFr || "",
      descriptionEn: item.descriptionEn || "",
      prixHt: item.prixHt ? String(item.prixHt) : "",
      prixDistribution: item.prixDistribution ? String(item.prixDistribution) : "",
      prixRevendeurPremium: item.prixRevendeurPremium ? String(item.prixRevendeurPremium) : "",
      prixRevendeur: item.prixRevendeur ? String(item.prixRevendeur) : "",
      prixClientFinal: item.prixClientFinal ? String(item.prixClientFinal) : "",
      poidsNet: item.poidsNet ? String(item.poidsNet) : "",
      poidsBrut: item.poidsBrut ? String(item.poidsBrut) : "",
      longueur: item.longueur ? String(item.longueur) : "",
      largeur: item.largeur ? String(item.largeur) : "",
      garantie: item.garantie,
      favori: item.favori ?? false,
      actif: item.actif,
      noteInterne: item.noteInterne || "",
      robotsCompatibles: item.robotsCompatibles || [],
      imageUrl: item.imageUrl || "",
      financement24m: item.financement24m ? String(item.financement24m) : "",
      financement36m: item.financement36m ? String(item.financement36m) : "",
      financement48m: item.financement48m ? String(item.financement48m) : "",
      maintenanceMensuelle: item.maintenanceMensuelle ? String(item.maintenanceMensuelle) : "",
      forfaitDeploiement: item.forfaitDeploiement ? String(item.forfaitDeploiement) : "",
      locationJourPartenaire: (item as any).locationJourPartenaire ? String((item as any).locationJourPartenaire) : "",
      locationSemainePartenaire: (item as any).locationSemainePartenaire ? String((item as any).locationSemainePartenaire) : "",
      locationMoisPartenaire: (item as any).locationMoisPartenaire ? String((item as any).locationMoisPartenaire) : "",
      locationJourClient: (item as any).locationJourClient ? String((item as any).locationJourClient) : "",
      locationSemaineClient: (item as any).locationSemaineClient ? String((item as any).locationSemaineClient) : "",
      locationMoisClient: (item as any).locationMoisClient ? String((item as any).locationMoisClient) : "",
      locationDisponible: (item as any).locationDisponible ?? false,
      locationDelaiInstallation: (item as any).locationDelaiInstallation || "",
      features: (item as any).features || [],
    });
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;
    try {
      await updateMutation.mutateAsync({ id: selectedItem.id, ...buildPayload(editData) });
      setEditOpen(false);
      toast({ title: "Produit modifié", description: "Les modifications ont été enregistrées" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message });
    }
  };

  const confirmDelete = (item: Product) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = () => {
    if (!itemToDelete) return;
    deleteMutation.mutate(itemToDelete.id, {
      onSuccess: () => {
        toast({ title: "Produit supprimé", description: `${itemToDelete.nomFr} a été supprimé.` });
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
      },
      onError: (err: any) => {
        toast({ variant: "destructive", title: "Erreur", description: err.message });
      },
    });
  };

  const openDetail = (item: Product) => {
    setSelectedItem(item);
    setDetailOpen(true);
  };

  return (
    <div className="space-y-6">
      {scannerOpen && <ProductScanner onClose={() => setScannerOpen(false)} />}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Produits</h1>
            {!isLoading && productsList && (
              <span className="text-sm font-medium text-muted-foreground bg-secondary/60 border border-border rounded-full px-3 py-0.5 tabular-nums" data-testid="text-products-total">
                {productsList.length.toLocaleString("fr-FR")}
              </span>
            )}
          </div>
          <p className="text-muted-foreground mt-1">Catalogue des pièces, consommables, machines et accessoires</p>
        </div>
        <div className="flex items-center gap-2">
          {cartItems.length > 0 && (
            <Button variant="outline" className="gap-2 relative" onClick={() => setCartModalOpen(true)} data-testid="button-open-cart">
              <ShoppingCart className="w-4 h-4" />
              Panier
              <span className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center leading-none">
                {cartItems.length}
              </span>
            </Button>
          )}
          {canScan && (
            <Button variant="outline" className="gap-2" onClick={() => setScannerOpen(true)} data-testid="button-open-scanner">
              <Scan className="w-4 h-4" />
              Scanner
            </Button>
          )}
          {canCreate && (
            <ImportDialog
              onDone={(ok, err) => { queryClient.invalidateQueries({ queryKey: ["/api/products"] }); toast({ title: "Import terminé", description: `${ok} produit(s) importé(s)${err ? ` · ${err} erreur(s)` : ""}.` }); }}
            />
          )}
          {canCreate && (
            <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) setFormData({ ...emptyForm }); }}>
              <DialogTrigger asChild>
                <Button className="shadow-lg shadow-primary/20" data-testid="button-add-product">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Produit
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nouveau Produit</DialogTitle>
                  <DialogDescription>Ajouter un nouveau produit au catalogue.</DialogDescription>
                </DialogHeader>
                <ProductForm data={formData} setData={setFormData} onSubmit={handleSubmit} isPending={createMutation.isPending} submitLabel="Créer le produit" onCancel={() => setCreateOpen(false)} />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 bg-card p-2 rounded-xl border shadow-sm flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="input-search-products"
            placeholder="Rechercher par nom ou SKU..."
            className="pl-9 pr-8 border-none bg-transparent focus-visible:ring-0"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Escape") resetFilters(); }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-clear-search"
              aria-label="Vider la recherche"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]" data-testid="select-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="robot">Robot</SelectItem>
            <SelectItem value="piece_detachee">Pièce détachée</SelectItem>
            <SelectItem value="consommable">Consommable</SelectItem>
            <SelectItem value="machine">Machine</SelectItem>
            <SelectItem value="accessoire">Accessoire</SelectItem>
            <SelectItem value="iot">IoT</SelectItem>
            <SelectItem value="service">Service</SelectItem>
          </SelectContent>
        </Select>
        <Select value={robotFilter} onValueChange={setRobotFilter}>
          <SelectTrigger className="w-[160px]" data-testid="select-robot-filter">
            <SelectValue placeholder="Modèle de robot" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les robots</SelectItem>
            {availableRobots.map(robot => (
              <SelectItem key={robot} value={robot}>{robot}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="w-[130px]" data-testid="select-active-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="actif">Actifs</SelectItem>
            <SelectItem value="inactif">Inactifs</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            data-testid="button-reset-filters"
            className="text-muted-foreground hover:text-foreground gap-1.5 h-8 px-3"
          >
            <X className="w-3.5 h-3.5" />
            Réinitialiser
          </Button>
        )}
        <div className="flex items-center gap-1 border rounded-lg p-1 bg-secondary/30">
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => toggleView("list")}
            data-testid="button-view-list"
          >
            <LayoutList className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => toggleView("grid")}
            data-testid="button-view-grid"
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Compteur dynamique */}
      {!isLoading && productsList && (
        <div className="flex items-center justify-between px-1" data-testid="text-products-count">
          <p className="text-sm text-muted-foreground">
            {hasActiveFilters ? (
              <><span className="font-semibold text-foreground">{filteredProducts?.length ?? 0}</span> produit{(filteredProducts?.length ?? 0) !== 1 ? "s" : ""} affiché{(filteredProducts?.length ?? 0) !== 1 ? "s" : ""} sur <span className="font-semibold text-foreground">{productsList.length}</span></>
            ) : (
              <><span className="font-semibold text-foreground">{productsList.length}</span> produit{productsList.length !== 1 ? "s" : ""} au total</>
            )}
          </p>
        </div>
      )}

      {viewMode === "list" ? (
        <TooltipProvider delayDuration={200}>
          <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                  <TableHead className="w-10 pl-3 pr-0"></TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Robots compatibles</TableHead>
                  <TableHead>Prix HT</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 px-2 text-xs font-semibold flex items-center gap-1 -ml-2 ${favoriSort ? "text-amber-500" : "text-muted-foreground"}`}
                      onClick={() => setFavoriSort(v => !v)}
                      data-testid="button-sort-favori"
                    >
                      <Star className={`w-3.5 h-3.5 ${favoriSort ? "fill-amber-400 text-amber-400" : ""}`} />
                      Favori
                      <ArrowUpDown className="w-3 h-3 opacity-60" />
                    </Button>
                  </TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-10 text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={9} className="text-center h-32">Chargement...</TableCell></TableRow>
                ) : filteredProducts?.map((item: Product) => (
                  <TableRow
                    key={item.id}
                    className={`group hover:bg-secondary/20 transition-colors cursor-pointer ${!item.actif ? 'opacity-50' : ''}`}
                    data-testid={`row-product-${item.id}`}
                    onClick={() => openDetail(item)}
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') openDetail(item); }}
                  >

                    {/* Photo miniature — cliquable pour lightbox */}
                    <TableCell className="pl-3 pr-0 py-2 w-10">
                      {item.imageUrl ? (
                        <button
                          type="button"
                          className="block rounded-lg overflow-hidden border shadow-sm shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          onClick={e => { e.stopPropagation(); setLightboxSrc(item.imageUrl!); }}
                          title="Agrandir l'image"
                          data-testid={`btn-thumbnail-${item.id}`}
                        >
                          <img src={item.imageUrl} alt={item.nomFr} className="w-10 h-10 object-cover hover:scale-110 transition-transform" />
                        </button>
                      ) : (
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center border bg-secondary/40 shrink-0">
                          <Package className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>

                    {/* Nom + garantie */}
                    <TableCell className="py-2">
                      <div className="font-semibold text-foreground leading-tight">{item.nomFr}</div>
                      {item.garantie && (
                        <Badge variant="outline" className="text-[10px] mt-0.5 border-emerald-300 text-emerald-700 dark:text-emerald-400">
                          <Check className="w-3 h-3 mr-0.5" /> Garantie
                        </Badge>
                      )}
                    </TableCell>

                    {/* Type */}
                    <TableCell className="py-2">
                      <Badge variant="outline" className="text-xs">{typeLabels[item.type] || item.type}</Badge>
                    </TableCell>

                    {/* SKU */}
                    <TableCell className="py-2">
                      <span className="font-mono text-sm text-muted-foreground">{item.sku}</span>
                    </TableCell>

                    {/* Robots compatibles — max 2 + +N tooltip */}
                    <TableCell className="py-2">
                      {item.robotsCompatibles && item.robotsCompatibles.length > 0 ? (
                        <div className="flex flex-wrap gap-1 items-center">
                          {item.robotsCompatibles.slice(0, 2).map((model: string, i: number) => (
                            <Badge key={i} variant="outline" className="text-xs">{model}</Badge>
                          ))}
                          {item.robotsCompatibles.length > 2 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="secondary" className="text-xs cursor-default">
                                  +{item.robotsCompatibles.length - 2}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[220px]">
                                <div className="flex flex-wrap gap-1">
                                  {item.robotsCompatibles.slice(2).map((r: string, i: number) => (
                                    <span key={i} className="bg-secondary text-foreground text-xs rounded px-1 py-0.5">{r}</span>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>

                    {/* Prix HT uniquement */}
                    <TableCell className="py-2 text-muted-foreground tabular-nums">
                      {item.prixHt ? `${parseFloat(item.prixHt).toFixed(2)} €` : '—'}
                    </TableCell>

                    {/* Favori — étoile cliquable */}
                    <TableCell className="py-2">
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); updateMutation.mutate({ id: item.id, favori: !item.favori }); }}
                        className="p-1 rounded hover:bg-secondary transition-colors"
                        data-testid={`button-favori-${item.id}`}
                        title={item.favori ? "Retirer des favoris" : "Ajouter aux favoris"}
                      >
                        <Star className={`w-4 h-4 transition-colors ${item.favori ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40 hover:text-amber-300"}`} />
                      </button>
                    </TableCell>

                    {/* Statut — point coloré + tooltip */}
                    <TableCell className="py-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${item.actif ? "bg-emerald-500" : "bg-muted-foreground/30"}`} data-testid={`status-dot-${item.id}`} />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {item.actif ? "Actif" : "Inactif"}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>

                    {/* Actions — menu contextuel ... */}
                    <TableCell className="py-2 text-right w-10">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost" size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`button-actions-${item.id}`}
                            onClick={e => e.stopPropagation()}
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => openDetail(item)} data-testid={`menu-view-${item.id}`}>
                            <Eye className="w-4 h-4 mr-2" /> Voir
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem onClick={() => openEdit(item)} data-testid={`menu-edit-${item.id}`}>
                              <Pencil className="w-4 h-4 mr-2" /> Modifier
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => updateMutation.mutate({ id: item.id, actif: !item.actif })}
                                data-testid={`menu-toggle-actif-${item.id}`}
                              >
                                {item.actif ? (
                                  <><span className="w-4 h-4 mr-2 flex items-center justify-center"><span className="w-2 h-2 rounded-full bg-muted-foreground/40" /></span> Désactiver</>
                                ) : (
                                  <><span className="w-4 h-4 mr-2 flex items-center justify-center"><span className="w-2 h-2 rounded-full bg-emerald-500" /></span> Activer</>
                                )}
                              </DropdownMenuItem>
                            </>
                          )}
                          {canDo(profile?.accessRole) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => confirmDelete(item)}
                                className="text-red-600 focus:text-red-600 dark:text-red-400"
                                data-testid={`menu-delete-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoading && (!filteredProducts || filteredProducts.length === 0) && (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Aucun produit trouvé</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TooltipProvider>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="grid-products">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border bg-card shadow-sm p-4 space-y-3 animate-pulse">
                <div className="aspect-square rounded-lg bg-secondary/50" />
                <div className="h-4 bg-secondary/50 rounded w-3/4" />
                <div className="h-3 bg-secondary/50 rounded w-1/2" />
              </div>
            ))
          ) : filteredProducts && filteredProducts.length > 0 ? (
            filteredProducts.map((item: Product) => (
              <div
                key={item.id}
                className={`group relative rounded-xl border bg-card shadow-sm overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/30 transition-all ${!item.actif ? 'opacity-60' : ''}`}
                onClick={() => openDetail(item)}
                data-testid={`card-product-${item.id}`}
              >
                {item.favori && (
                  <div className="absolute top-2 right-2 z-10 pointer-events-none">
                    <Star className="w-5 h-5 fill-amber-400 text-amber-400 drop-shadow" data-testid={`icon-favori-card-${item.id}`} />
                  </div>
                )}
                <div className="aspect-square bg-secondary/20 flex items-center justify-center overflow-hidden">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.nomFr} className="w-full h-full object-cover" />
                  ) : (
                    <Package className="w-12 h-12 text-muted-foreground/40" />
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <p className="font-mono text-[10px] text-muted-foreground">{item.sku}</p>
                  <h3 className="font-semibold text-sm leading-tight line-clamp-2">{item.nomFr}</h3>
                  {item.robotsCompatibles && item.robotsCompatibles.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.robotsCompatibles.map((r: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">{r}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center gap-1.5">
                      {item.prixHt && (
                        <span className="text-sm font-bold text-foreground">
                          {parseFloat(item.prixHt).toFixed(2)} €
                        </span>
                      )}
                      {item.garantie && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-300 text-emerald-700 dark:text-emerald-400">
                          <Check className="w-2.5 h-2.5 mr-0.5" />Garantie
                        </Badge>
                      )}
                    </div>
                    <Badge variant={item.actif ? "default" : "secondary"} className={`text-[10px] px-1.5 py-0 ${item.actif ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100' : ''}`}>
                      {item.actif ? "Actif" : "Inactif"}
                    </Badge>
                  </div>
                </div>
                {canEdit && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canEdit && (
                      <Button variant="secondary" size="sm" className="h-7 w-7 p-0 shadow-sm" onClick={e => { e.stopPropagation(); openEdit(item); }} data-testid={`button-edit-product-card-${item.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                    {canDo(profile?.accessRole) && (
                      <Button variant="secondary" size="sm" className="h-7 w-7 p-0 shadow-sm" onClick={e => { e.stopPropagation(); confirmDelete(item); }} data-testid={`button-delete-product-card-${item.id}`}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              Aucun produit trouvé
            </div>
          )}
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Détails du produit</DialogTitle>
            <DialogDescription>{selectedItem?.nomFr}</DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <ProductDetail
              product={selectedItem}
              onAddToCart={() => {
                addToCart(selectedItem);
                setDetailOpen(false);
                setCartModalOpen(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox liste */}
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />

      {/* Panier devis */}
      <DevisCartModal
        open={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        onAddMore={() => setCartModalOpen(false)}
        onNavigate={() => { setCartModalOpen(false); navigate("/devis/nouveau"); }}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le produit</DialogTitle>
            <DialogDescription>Modifier les informations du produit.</DialogDescription>
          </DialogHeader>
          <ProductForm data={editData} setData={setEditData} onSubmit={handleEdit} isPending={updateMutation.isPending} submitLabel="Enregistrer" onCancel={() => setEditOpen(false)} />
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le produit <strong>{itemToDelete?.nomFr}</strong> ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" disabled={deleteMutation.isPending} data-testid="button-confirm-delete">
              {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProductForm({
  data,
  setData,
  onSubmit,
  isPending,
  submitLabel,
  onCancel,
}: {
  data: FormData;
  setData: (d: FormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
  submitLabel: string;
  onCancel: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authFetch("/api/products/upload-image", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Erreur upload" }));
        throw new Error(err.message);
      }
      const { url } = await res.json();
      setData({ ...data, imageUrl: url });
      toast({ title: "Image uploadée" });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur upload", description: err.message });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleRobot = (robot: string) => {
    const current = data.robotsCompatibles;
    if (current.includes(robot)) {
      setData({ ...data, robotsCompatibles: current.filter(r => r !== robot) });
    } else {
      setData({ ...data, robotsCompatibles: [...current, robot] });
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 pt-4">
      <div>
        <Label>Image du produit</Label>
        <div className="flex items-center gap-4 mt-2">
          {data.imageUrl ? (
            <div className="relative">
              <img src={data.imageUrl} alt="Aperçu" className="w-20 h-20 rounded-lg object-cover border" />
              <button
                type="button"
                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center"
                onClick={() => setData({ ...data, imageUrl: "" })}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center text-muted-foreground">
              <Package className="w-8 h-8" />
            </div>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
              data-testid="input-product-image"
            />
            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Upload..." : "Choisir une image"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nom (français) *</Label>
          <Input data-testid="input-product-nom-fr" required value={data.nomFr} onChange={e => setData({ ...data, nomFr: e.target.value })} placeholder="Batterie BellaBot" />
        </div>
        <div className="space-y-2">
          <Label>Nom (anglais)</Label>
          <Input data-testid="input-product-nom-en" value={data.nomEn} onChange={e => setData({ ...data, nomEn: e.target.value })} placeholder="BellaBot Battery" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>SKU *</Label>
          <Input data-testid="input-product-sku" required value={data.sku} onChange={e => setData({ ...data, sku: e.target.value })} placeholder="BAT-001" />
        </div>
        <div className="space-y-2">
          <Label>Type *</Label>
          <Select value={data.type} onValueChange={v => setData({ ...data, type: v })}>
            <SelectTrigger data-testid="select-product-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="robot">Robot</SelectItem>
              <SelectItem value="piece_detachee">Pièce détachée</SelectItem>
              <SelectItem value="consommable">Consommable</SelectItem>
              <SelectItem value="machine">Machine</SelectItem>
              <SelectItem value="accessoire">Accessoire</SelectItem>
              <SelectItem value="iot">IoT</SelectItem>
              <SelectItem value="service">Service</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {data.type === "service" && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-2">Prestations incluses (bullets)</p>
          <div className="space-y-2">
            {data.features.map((feat, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Input
                  data-testid={`input-service-bullet-${idx}`}
                  value={feat}
                  onChange={e => {
                    const updated = [...data.features];
                    updated[idx] = e.target.value;
                    setData({ ...data, features: updated });
                  }}
                  placeholder={`Bullet ${idx + 1}`}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => setData({ ...data, features: data.features.filter((_, i) => i !== idx) })}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  data-testid={`button-remove-bullet-${idx}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full mt-1"
              data-testid="button-add-bullet"
              onClick={() => setData({ ...data, features: [...data.features, ""] })}
            >
              + Ajouter un bullet
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-2">Tarification (€ HT)</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Prix HT (base)</Label>
            <div className="relative">
              <Input data-testid="input-product-prix-ht" type="number" step="0.01" min="0" value={data.prixHt} onChange={e => setData({ ...data, prixHt: e.target.value })} placeholder="0.00" className="pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Prix distribution</Label>
            <div className="relative">
              <Input data-testid="input-product-prix-distribution" type="number" step="0.01" min="0" value={data.prixDistribution} onChange={e => setData({ ...data, prixDistribution: e.target.value })} placeholder="0.00" className="pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Prix revendeur premium</Label>
            <div className="relative">
              <Input data-testid="input-product-prix-rev-premium" type="number" step="0.01" min="0" value={data.prixRevendeurPremium} onChange={e => setData({ ...data, prixRevendeurPremium: e.target.value })} placeholder="0.00" className="pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Prix revendeur</Label>
            <div className="relative">
              <Input data-testid="input-product-prix-revendeur" type="number" step="0.01" min="0" value={data.prixRevendeur} onChange={e => setData({ ...data, prixRevendeur: e.target.value })} placeholder="0.00" className="pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Prix client final</Label>
            <div className="relative">
              <Input data-testid="input-product-prix-client" type="number" step="0.01" min="0" value={data.prixClientFinal} onChange={e => setData({ ...data, prixClientFinal: e.target.value })} placeholder="0.00" className="pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
            </div>
          </div>
        </div>
      </div>

      {data.type === "machine" && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-2">Financement et déploiement</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Mensualité 24 mois (€/mois)</Label>
              <div className="relative">
                <Input data-testid="input-product-fin-24" type="number" step="0.01" min="0" value={data.financement24m} onChange={e => setData({ ...data, financement24m: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Mensualité 36 mois (€/mois)</Label>
              <div className="relative">
                <Input data-testid="input-product-fin-36" type="number" step="0.01" min="0" value={data.financement36m} onChange={e => setData({ ...data, financement36m: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Mensualité 48 mois (€/mois) — optionnel</Label>
              <div className="relative">
                <Input data-testid="input-product-fin-48" type="number" step="0.01" min="0" value={data.financement48m} onChange={e => setData({ ...data, financement48m: e.target.value })} placeholder="Non applicable" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Maintenance mensuelle conseillée (€/mois)</Label>
              <div className="relative">
                <Input data-testid="input-product-maintenance" type="number" step="0.01" min="0" value={data.maintenanceMensuelle} onChange={e => setData({ ...data, maintenanceMensuelle: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Forfait déploiement (€ HT)</Label>
              <div className="relative">
                <Input data-testid="input-product-forfait" type="number" step="0.01" min="0" value={data.forfaitDeploiement} onChange={e => setData({ ...data, forfaitDeploiement: e.target.value })} placeholder="Sur devis" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {data.type === "machine" && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-2">Tarifs de location</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="switch-location-disponible"
                  id="location-dispo"
                  checked={data.locationDisponible}
                  onCheckedChange={v => setData({ ...data, locationDisponible: v })}
                />
                <Label htmlFor="location-dispo" className="text-xs font-medium cursor-pointer">
                  {data.locationDisponible ? "Disponible" : "Sur demande"}
                </Label>
              </div>
              <div className="flex-1">
                <Input
                  data-testid="input-location-delai"
                  type="text"
                  value={data.locationDelaiInstallation}
                  onChange={e => setData({ ...data, locationDelaiInstallation: e.target.value })}
                  placeholder="Délai d'installation (ex: 1 jour)"
                  className="text-xs"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Location/jour — Partenaire (€)</Label>
              <div className="relative">
                <Input data-testid="input-loc-jour-p" type="number" step="0.01" min="0" value={data.locationJourPartenaire} onChange={e => setData({ ...data, locationJourPartenaire: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Location/jour — Client final (€)</Label>
              <div className="relative">
                <Input data-testid="input-loc-jour-c" type="number" step="0.01" min="0" value={data.locationJourClient} onChange={e => setData({ ...data, locationJourClient: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Location/semaine — Partenaire (€)</Label>
              <div className="relative">
                <Input data-testid="input-loc-sem-p" type="number" step="0.01" min="0" value={data.locationSemainePartenaire} onChange={e => setData({ ...data, locationSemainePartenaire: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Location/semaine — Client final (€)</Label>
              <div className="relative">
                <Input data-testid="input-loc-sem-c" type="number" step="0.01" min="0" value={data.locationSemaineClient} onChange={e => setData({ ...data, locationSemaineClient: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Location/mois — Partenaire (€)</Label>
              <div className="relative">
                <Input data-testid="input-loc-mois-p" type="number" step="0.01" min="0" value={data.locationMoisPartenaire} onChange={e => setData({ ...data, locationMoisPartenaire: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Location/mois — Client final (€)</Label>
              <div className="relative">
                <Input data-testid="input-loc-mois-c" type="number" step="0.01" min="0" value={data.locationMoisClient} onChange={e => setData({ ...data, locationMoisClient: e.target.value })} placeholder="0.00" className="pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Poids net (g)</Label>
          <Input data-testid="input-product-poids-net" type="number" min="0" value={data.poidsNet} onChange={e => setData({ ...data, poidsNet: e.target.value })} placeholder="0" />
        </div>
        <div className="space-y-2">
          <Label>Poids brut (g)</Label>
          <Input data-testid="input-product-poids-brut" type="number" min="0" value={data.poidsBrut} onChange={e => setData({ ...data, poidsBrut: e.target.value })} placeholder="0" />
        </div>
        <div className="space-y-2">
          <Label>Longueur (mm)</Label>
          <Input data-testid="input-product-longueur" type="number" min="0" value={data.longueur} onChange={e => setData({ ...data, longueur: e.target.value })} placeholder="0" />
        </div>
        <div className="space-y-2">
          <Label>Largeur (mm)</Label>
          <Input data-testid="input-product-largeur" type="number" min="0" value={data.largeur} onChange={e => setData({ ...data, largeur: e.target.value })} placeholder="0" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Robots compatibles</Label>
        <div className="flex flex-wrap gap-2" data-testid="select-robots-compatibles">
          {COMPATIBLE_ROBOTS.map(robot => {
            const selected = data.robotsCompatibles.includes(robot);
            return (
              <Badge
                key={robot}
                variant={selected ? "default" : "outline"}
                className={`cursor-pointer transition-colors ${selected ? '' : 'hover:bg-secondary'}`}
                onClick={() => toggleRobot(robot)}
                data-testid={`badge-robot-${robot}`}
              >
                {robot}
                {selected && <X className="w-3 h-3 ml-1" />}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description (français)</Label>
        <Textarea data-testid="textarea-product-description-fr" value={data.descriptionFr} onChange={e => setData({ ...data, descriptionFr: e.target.value })} rows={2} placeholder="Description du produit en français..." />
      </div>

      <div className="space-y-2">
        <Label>Description (anglais)</Label>
        <Textarea data-testid="textarea-product-description-en" value={data.descriptionEn} onChange={e => setData({ ...data, descriptionEn: e.target.value })} rows={2} placeholder="Product description in English..." />
      </div>

      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <Switch
            checked={data.garantie}
            onCheckedChange={v => setData({ ...data, garantie: v })}
            data-testid="switch-garantie"
          />
          <Label>Garantie</Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={data.favori}
            onCheckedChange={v => setData({ ...data, favori: v })}
            data-testid="switch-favori"
          />
          <Label className="flex items-center gap-1">
            <Star className={`w-3.5 h-3.5 ${data.favori ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
            Favori
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={data.actif}
            onCheckedChange={v => setData({ ...data, actif: v })}
            data-testid="switch-actif"
          />
          <Label>Actif</Label>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Note interne</Label>
        <Textarea
          data-testid="textarea-note-interne"
          value={data.noteInterne}
          onChange={e => setData({ ...data, noteInterne: e.target.value })}
          rows={2}
          placeholder="Note visible uniquement en interne..."
          className="bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
        />
      </div>

      <div className="pt-4 flex justify-end gap-2">
        <Button variant="outline" type="button" onClick={onCancel}>Annuler</Button>
        <Button type="submit" disabled={isPending || !data.type} data-testid="button-submit-product">
          {isPending ? "Enregistrement..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function ImageLightbox({ src, onClose }: { src: string | null; onClose: () => void }) {
  useEffect(() => {
    if (!src) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [src, onClose]);
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button
        type="button"
        className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 transition-colors"
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Fermer"
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={src}
        alt=""
        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="lightbox-image"
      />
    </div>
  );
}

function DevisCartModal({ open, onClose, onAddMore, onNavigate }: {
  open: boolean;
  onClose: () => void;
  onAddMore: () => void;
  onNavigate: () => void;
}) {
  const { items, removeFromCart, updateQuantite, clearCart } = useDevisCart();

  const totalHt = items.reduce((sum, i) => sum + (Number(i.product.prixHt) || 0) * i.quantite, 0);
  const fmtEur = (n: number) => n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary" />
            Panier devis
            <span className="text-sm font-normal text-muted-foreground">({items.length} produit{items.length !== 1 ? "s" : ""})</span>
          </DialogTitle>
          <DialogDescription>Vérifiez votre sélection avant de créer le devis.</DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Le panier est vide</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
            {items.map(({ product, quantite }) => (
              <div key={product.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-card" data-testid={`cart-item-${product.id}`}>
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt={product.nomFr} className="w-12 h-12 object-cover rounded-md border shrink-0" />
                ) : (
                  <div className="w-12 h-12 bg-muted rounded-md border flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{product.nomFr}</p>
                  {product.sku && <p className="text-xs text-muted-foreground font-mono">{product.sku}</p>}
                  {product.prixHt && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      PU HT : {fmtEur(Number(product.prixHt))}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    className="w-6 h-6 rounded border border-border bg-background hover:bg-muted flex items-center justify-center transition-colors"
                    onClick={() => updateQuantite(product.id, quantite - 1)}
                    disabled={quantite <= 1}
                    data-testid={`cart-qty-minus-${product.id}`}
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="w-7 text-center text-sm font-medium tabular-nums" data-testid={`cart-qty-${product.id}`}>{quantite}</span>
                  <button
                    className="w-6 h-6 rounded border border-border bg-background hover:bg-muted flex items-center justify-center transition-colors"
                    onClick={() => updateQuantite(product.id, quantite + 1)}
                    data-testid={`cart-qty-plus-${product.id}`}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <button
                    className="w-6 h-6 rounded border border-border bg-background hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex items-center justify-center transition-colors ml-1"
                    onClick={() => removeFromCart(product.id)}
                    data-testid={`cart-remove-${product.id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-border pt-3 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total estimé HT</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="cart-total-ht">{fmtEur(totalHt)}</p>
            </div>
            <button
              className="text-xs text-muted-foreground hover:text-destructive underline underline-offset-2 transition-colors"
              onClick={() => { clearCart(); onClose(); }}
              data-testid="button-cart-clear"
            >
              Vider le panier
            </button>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1 gap-2" onClick={onAddMore} data-testid="button-cart-add-more">
            <Plus className="w-4 h-4" />
            Ajouter d'autres produits
          </Button>
          <Button
            className="flex-1 gap-2"
            onClick={onNavigate}
            disabled={items.length === 0}
            data-testid="button-cart-create-devis"
          >
            <FileText className="w-4 h-4" />
            Créer le devis
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SkuHistoryBlock({ productId, canAdmin }: { productId: string; canAdmin: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newSku, setNewSku] = useState("");
  const [couleur, setCouleur] = useState("");

  const { data: skus = [], isLoading } = useQuery<ProductSku[]>({
    queryKey: ["/api/products", productId, "skus"],
    queryFn: () => authFetch(`/api/products/${productId}/skus`).then(r => r.json()),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: (body: { newSku: string; couleur?: string }) =>
      authFetch(`/api/products/${productId}/skus/current`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(async r => {
        if (!r.ok) { const d = await r.json(); throw new Error(d.message); }
        return r.json();
      }),
    onSuccess: () => {
      toast({ title: "SKU mis à jour" });
      setEditing(false);
      setNewSku("");
      setCouleur("");
      queryClient.invalidateQueries({ queryKey: ["/api/products", productId, "skus"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const current = skus.find(s => s.isCurrent);

  return (
    <div className="space-y-2">
      <button
        type="button"
        data-testid="btn-sku-history-toggle"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors w-full text-left"
      >
        <History className="w-3.5 h-3.5" />
        Historique SKU
        {open ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
      </button>

      {open && (
        <div className="rounded-lg border border-border overflow-hidden text-xs">
          {isLoading ? (
            <div className="p-3 text-muted-foreground animate-pulse">Chargement…</div>
          ) : skus.length === 0 ? (
            <div className="p-3 text-muted-foreground italic">Aucun SKU enregistré dans l'historique.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-muted/40">
                <tr>
                  <th className="p-2 text-left font-medium text-muted-foreground">SKU</th>
                  <th className="p-2 text-left font-medium text-muted-foreground">Couleur</th>
                  <th className="p-2 text-left font-medium text-muted-foreground">Statut</th>
                  <th className="p-2 text-left font-medium text-muted-foreground">Source</th>
                  <th className="p-2 text-left font-medium text-muted-foreground">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {skus.map(s => (
                  <tr key={s.id} className={s.isCurrent ? "bg-primary/5" : ""}>
                    <td className="p-2 font-mono font-medium">{s.sku}</td>
                    <td className="p-2 text-muted-foreground">{s.couleur || "—"}</td>
                    <td className="p-2">
                      {s.isCurrent
                        ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium"><Check className="w-3 h-3" />Courant</span>
                        : <span className="text-muted-foreground">Historique</span>}
                    </td>
                    <td className="p-2 text-muted-foreground">{s.source || "—"}</td>
                    <td className="p-2 text-muted-foreground">{s.dateAjout ? new Date(s.dateAjout).toLocaleDateString("fr-FR") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {canAdmin && (
            <div className="border-t border-border/60 bg-muted/20 p-2">
              {!editing ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="btn-edit-sku"
                  className="text-xs h-7"
                  onClick={() => { setEditing(true); setNewSku(current?.sku ?? ""); setCouleur(current?.couleur ?? ""); }}
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Modifier le SKU courant
                </Button>
              ) : (
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">Nouveau SKU *</Label>
                    <Input
                      data-testid="input-new-sku"
                      value={newSku}
                      onChange={e => setNewSku(e.target.value)}
                      className="h-7 text-xs font-mono w-44"
                      placeholder="ex: 10080-000042"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Couleur (optionnel)</Label>
                    <Input
                      data-testid="input-sku-couleur"
                      value={couleur}
                      onChange={e => setCouleur(e.target.value)}
                      className="h-7 text-xs w-28"
                      placeholder="Noir, Blanc…"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    data-testid="btn-save-sku"
                    className="h-7 text-xs"
                    disabled={!newSku.trim() || mutation.isPending}
                    onClick={() => mutation.mutate({ newSku, couleur: couleur || undefined })}
                  >
                    {mutation.isPending ? "…" : "Enregistrer"}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)}>
                    Annuler
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProductDetail({ product, onAddToCart }: { product: Product; onAddToCart?: () => void }) {
  const { profile } = useAuth();
  const fmt = (v: string | null | undefined) => v ? `${parseFloat(v).toFixed(2)} €` : '—';
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const { data: extensions } = useQuery<any[]>({
    queryKey: ["/api/extensions-garantie/by-product", product.id],
    queryFn: () => authFetch(`/api/extensions-garantie/by-product/${product.id}`).then(r => r.json()),
    enabled: product.type === "machine",
  });
  return (
    <div className="space-y-4 pt-2">
      {product.imageUrl && (
        <div className="flex justify-center">
          <button
            type="button"
            className="rounded-lg overflow-hidden border shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-zoom-in"
            onClick={() => setLightboxSrc(product.imageUrl!)}
            title="Agrandir l'image"
            data-testid="btn-product-image-detail"
          >
            <img src={product.imageUrl} alt={product.nomFr} className="max-w-[200px] max-h-[200px] object-cover hover:scale-105 transition-transform" />
          </button>
          <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="text-muted-foreground">Nom (FR) :</span> <span className="font-medium">{product.nomFr}</span></div>
        {product.nomEn && <div><span className="text-muted-foreground">Nom (EN) :</span> <span className="font-medium">{product.nomEn}</span></div>}
        <div><span className="text-muted-foreground">SKU :</span> <span className="font-mono">{product.sku ?? <span className="italic text-muted-foreground/60">À définir</span>}</span></div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Type :</span> {typeLabels[product.type] || product.type}
          {(product as any).categorieRobot && (
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${categorieRobotColors[(product as any).categorieRobot] || "bg-muted text-muted-foreground"}`}>
              {(product as any).categorieRobot}
            </span>
          )}
        </div>
        <div>
          <span className="text-muted-foreground">Garantie :</span>{" "}
          {product.garantie ? (
            <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700 dark:text-emerald-400">Oui</Badge>
          ) : "Non"}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Favori :</span>{" "}
          {product.favori
            ? <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            : <span className="text-muted-foreground/60 text-xs">Non</span>}
        </div>
        <div>
          <span className="text-muted-foreground">Statut :</span>{" "}
          <Badge variant={product.actif ? "default" : "secondary"} className={`text-xs ${product.actif ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100' : ''}`}>
            {product.actif ? "Actif" : "Inactif"}
          </Badge>
        </div>
      </div>
      {product.type === "robot" && (
        <div className="space-y-4">
          {/* Caractéristiques + Spécifications */}
          {((product as any).features?.length || (product as any).specifications?.length) && (
            <div className="grid grid-cols-2 gap-3">
              {(product as any).features?.length > 0 && (
                <div className="bg-secondary/20 rounded-lg p-3 border border-border">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Caractéristiques</p>
                  <ul className="space-y-1">
                    {(product as any).features.map((f: string, i: number) => (
                      <li key={i} className="text-xs flex items-start gap-1.5">
                        <span className="text-primary mt-0.5 shrink-0">•</span>{f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(product as any).specifications?.length > 0 && (
                <div className="bg-secondary/20 rounded-lg p-3 border border-border">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Spécifications</p>
                  <ul className="space-y-1">
                    {(product as any).specifications.map((s: string, i: number) => (
                      <li key={i} className="text-xs flex items-start gap-1.5">
                        <span className="text-primary mt-0.5 shrink-0">•</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {/* Grille tarifaire complète */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Grille tarifaire</p>
            <div className="overflow-hidden rounded-lg border border-border text-xs">
              <table className="w-full">
                <tbody className="divide-y divide-border/40">
                  {(product as any).prixSousDistributeur && (
                    <tr>
                      <td className="p-2.5 text-muted-foreground">Sous-distributeur / Revendeur Premium</td>
                      <td className="p-2.5 text-right font-semibold">{fmt((product as any).prixSousDistributeur)}</td>
                    </tr>
                  )}
                  {(product as any).prixRevendeurN2 && (
                    <tr>
                      <td className="p-2.5 text-muted-foreground">Revendeur niveau 2</td>
                      <td className="p-2.5 text-right font-semibold">{fmt((product as any).prixRevendeurN2)}</td>
                    </tr>
                  )}
                  {(product as any).prixMinPudu && (
                    <tr>
                      <td className="p-2.5 text-muted-foreground">Prix min. Pudu</td>
                      <td className="p-2.5 text-right font-semibold">{fmt((product as any).prixMinPudu)}</td>
                    </tr>
                  )}
                  {(product as any).prixAchatConseille && (
                    <tr>
                      <td className="p-2.5 text-muted-foreground">Prix achat conseillé</td>
                      <td className="p-2.5 text-right font-semibold">{fmt((product as any).prixAchatConseille)}</td>
                    </tr>
                  )}
                  {((product as any).loc24Mois || (product as any).loc36Mois || (product as any).loc48Mois) && (
                    <tr className="bg-muted/20"><td colSpan={2} className="p-2 text-muted-foreground font-medium text-xs">Location longue durée</td></tr>
                  )}
                  {(product as any).loc24Mois && (
                    <tr>
                      <td className="p-2.5 pl-4 text-muted-foreground">Location 24 mois</td>
                      <td className="p-2.5 text-right font-semibold">{fmt((product as any).loc24Mois)}<span className="text-muted-foreground font-normal">/mois</span></td>
                    </tr>
                  )}
                  {(product as any).loc36Mois && (
                    <tr>
                      <td className="p-2.5 pl-4 text-muted-foreground">Location 36 mois</td>
                      <td className="p-2.5 text-right font-semibold">{fmt((product as any).loc36Mois)}<span className="text-muted-foreground font-normal">/mois</span></td>
                    </tr>
                  )}
                  {(product as any).loc48Mois && (
                    <tr>
                      <td className="p-2.5 pl-4 text-muted-foreground">Location 48 mois</td>
                      <td className="p-2.5 text-right font-semibold">{fmt((product as any).loc48Mois)}<span className="text-muted-foreground font-normal">/mois</span></td>
                    </tr>
                  )}
                  {(product.maintenanceMensuelle || product.forfaitDeploiement) && (
                    <tr className="bg-muted/20"><td colSpan={2} className="p-2 text-muted-foreground font-medium text-xs">Services</td></tr>
                  )}
                  {product.maintenanceMensuelle && (
                    <tr>
                      <td className="p-2.5 pl-4 text-muted-foreground">Maintenance mensuelle conseillée</td>
                      <td className="p-2.5 text-right font-semibold">{fmt(product.maintenanceMensuelle)}<span className="text-muted-foreground font-normal">/mois</span></td>
                    </tr>
                  )}
                  {product.forfaitDeploiement && (
                    <tr>
                      <td className="p-2.5 pl-4 text-muted-foreground">Forfait déploiement</td>
                      <td className="p-2.5 text-right font-semibold">{fmt(product.forfaitDeploiement)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Historique SKU */}
          <SkuHistoryBlock productId={product.id} canAdmin={canDo(profile?.accessRole)} />
        </div>
      )}

      {product.type === "machine" ? (
        (product.prixHt || product.prixRevendeurPremium || product.prixRevendeur || product.prixClientFinal || product.financement24m || product.maintenanceMensuelle) && (
          <div className="text-sm space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tarifs et financement</p>
            <div className="space-y-2">
              <p className="text-xs font-medium text-foreground/70">Tarifs</p>
              <div className="grid grid-cols-2 gap-2 bg-secondary/30 rounded-lg p-3">
                {product.prixHt && <div><span className="text-muted-foreground">Prix HT minimum :</span> <span className="font-medium">{fmt(product.prixHt)}</span></div>}
                {product.prixClientFinal && <div><span className="text-muted-foreground">Prix conseillé client :</span> <span className="font-medium">{fmt(product.prixClientFinal)}</span></div>}
                {product.prixRevendeurPremium && <div><span className="text-muted-foreground">Prix revendeur premium :</span> <span className="font-medium">{fmt(product.prixRevendeurPremium)}</span></div>}
                {product.prixRevendeur && <div><span className="text-muted-foreground">Prix revendeur :</span> <span className="font-medium">{fmt(product.prixRevendeur)}</span></div>}
              </div>
            </div>
            {(product.financement24m || product.financement36m || product.financement48m) && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground/70">Options de financement (mensualités HT)</p>
                <div className="grid grid-cols-3 gap-2 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900 rounded-lg p-3">
                  {product.financement24m && (
                    <div className="text-center">
                      <div className="font-semibold text-base">{fmt(product.financement24m)}<span className="text-xs text-muted-foreground">/mois</span></div>
                      <div className="text-xs text-muted-foreground mt-0.5">24 mois</div>
                    </div>
                  )}
                  {product.financement36m && (
                    <div className="text-center">
                      <div className="font-semibold text-base">{fmt(product.financement36m)}<span className="text-xs text-muted-foreground">/mois</span></div>
                      <div className="text-xs text-muted-foreground mt-0.5">36 mois</div>
                    </div>
                  )}
                  <div className="text-center">
                    {product.financement48m ? (
                      <>
                        <div className="font-semibold text-base">{fmt(product.financement48m)}<span className="text-xs text-muted-foreground">/mois</span></div>
                        <div className="text-xs text-muted-foreground mt-0.5">48 mois</div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-muted-foreground italic mt-3">Non applicable</div>
                        <div className="text-xs text-muted-foreground mt-0.5">48 mois</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {(product.maintenanceMensuelle || product.forfaitDeploiement) && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground/70">Services</p>
                <div className="grid grid-cols-2 gap-2 bg-secondary/30 rounded-lg p-3">
                  {product.maintenanceMensuelle && (
                    <div><span className="text-muted-foreground">Maintenance mensuelle conseillée :</span> <span className="font-medium">{fmt(product.maintenanceMensuelle)}/mois</span></div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Forfait déploiement :</span>{" "}
                    <span className="font-medium">{product.forfaitDeploiement ? fmt(product.forfaitDeploiement) : "Sur devis"}</span>
                  </div>
                </div>
              </div>
            )}
            {((product as any).locationJourPartenaire || (product as any).locationJourClient) && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-foreground/70">Tarifs de location</p>
                  {(product as any).locationDisponible ? (
                    <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">✅ Disponible</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Sur demande</Badge>
                  )}
                </div>
                <div className="overflow-hidden rounded-lg border border-border text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/40">
                        <th className="text-left p-2 font-medium text-muted-foreground">Période</th>
                        <th className="text-right p-2 font-medium text-muted-foreground">Partenaire</th>
                        <th className="text-right p-2 font-medium text-muted-foreground">Client final</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {(product as any).locationJourPartenaire && (
                        <tr>
                          <td className="p-2 text-muted-foreground">Journalière</td>
                          <td className="p-2 text-right font-medium">{fmt((product as any).locationJourPartenaire)}</td>
                          <td className="p-2 text-right font-medium">{(product as any).locationJourClient ? fmt((product as any).locationJourClient) : "—"}</td>
                        </tr>
                      )}
                      {(product as any).locationSemainePartenaire && (
                        <tr>
                          <td className="p-2 text-muted-foreground">Hebdomadaire</td>
                          <td className="p-2 text-right font-medium">{fmt((product as any).locationSemainePartenaire)}</td>
                          <td className="p-2 text-right font-medium">{(product as any).locationSemaineClient ? fmt((product as any).locationSemaineClient) : "—"}</td>
                        </tr>
                      )}
                      {(product as any).locationMoisPartenaire && (
                        <tr>
                          <td className="p-2 text-muted-foreground">Mensuelle</td>
                          <td className="p-2 text-right font-medium">{fmt((product as any).locationMoisPartenaire)}</td>
                          <td className="p-2 text-right font-medium">{(product as any).locationMoisClient ? fmt((product as any).locationMoisClient) : "—"}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {(product as any).locationDelaiInstallation && (
                  <p className="text-xs text-muted-foreground">Délai d'installation : {(product as any).locationDelaiInstallation}</p>
                )}
              </div>
            )}
            {extensions && extensions.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground/70">Extensions de garantie</p>
                <div className="overflow-hidden rounded-lg border border-border text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/40">
                        <th className="text-left p-2 font-medium text-muted-foreground">Extension</th>
                        <th className="text-right p-2 font-medium text-muted-foreground">Prix revendeur</th>
                        <th className="text-right p-2 font-medium text-muted-foreground">Prix client final</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {extensions.map((ext: any) => (
                        <tr key={ext.id}>
                          <td className="p-2 text-foreground/80">{ext.libelle}</td>
                          <td className="p-2 text-right font-medium">{ext.prixRevendeur ? fmt(ext.prixRevendeur) : "—"}</td>
                          <td className="p-2 text-right font-medium">{ext.prixClientFinal ? fmt(ext.prixClientFinal) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )
      ) : (
        (product.prixHt || product.prixDistribution || product.prixRevendeurPremium || product.prixRevendeur || product.prixClientFinal) && (
          <div className="text-sm space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tarification</p>
            <div className="grid grid-cols-2 gap-2 bg-secondary/30 rounded-lg p-3">
              {product.prixHt && <div><span className="text-muted-foreground">Prix HT (base) :</span> <span className="font-medium">{fmt(product.prixHt)}</span></div>}
              {product.prixDistribution && <div><span className="text-muted-foreground">Distribution :</span> <span className="font-medium">{fmt(product.prixDistribution)}</span></div>}
              {product.prixRevendeurPremium && <div><span className="text-muted-foreground">Rev. premium :</span> <span className="font-medium">{fmt(product.prixRevendeurPremium)}</span></div>}
              {product.prixRevendeur && <div><span className="text-muted-foreground">Revendeur :</span> <span className="font-medium">{fmt(product.prixRevendeur)}</span></div>}
              {product.prixClientFinal && <div><span className="text-muted-foreground">Client final :</span> <span className="font-medium">{fmt(product.prixClientFinal)}</span></div>}
            </div>
          </div>
        )
      )}
      {(product.poidsNet || product.poidsBrut || product.longueur || product.largeur) && (
        <div className="grid grid-cols-2 gap-2 text-sm">
          {product.poidsNet && <div><span className="text-muted-foreground">Poids net :</span> <span className="font-medium">{product.poidsNet} g</span></div>}
          {product.poidsBrut && <div><span className="text-muted-foreground">Poids brut :</span> <span className="font-medium">{product.poidsBrut} g</span></div>}
          {product.longueur && <div><span className="text-muted-foreground">Longueur :</span> <span className="font-medium">{product.longueur} mm</span></div>}
          {product.largeur && <div><span className="text-muted-foreground">Largeur :</span> <span className="font-medium">{product.largeur} mm</span></div>}
        </div>
      )}
      {product.robotsCompatibles && product.robotsCompatibles.length > 0 && (
        <div className="text-sm">
          <span className="text-muted-foreground">Robots compatibles :</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {product.robotsCompatibles.map((m, i) => <Badge key={i} variant="outline">{m}</Badge>)}
          </div>
        </div>
      )}
      {product.descriptionFr && (
        <div className="text-sm">
          <span className="text-muted-foreground">Description (FR) :</span>
          <p className="mt-1">{product.descriptionFr}</p>
        </div>
      )}
      {product.descriptionEn && (
        <div className="text-sm">
          <span className="text-muted-foreground">Description (EN) :</span>
          <p className="mt-1">{product.descriptionEn}</p>
        </div>
      )}
      {product.noteInterne && (
        <div className="text-sm p-3 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
          <span className="text-muted-foreground font-medium">Note interne :</span>
          <p className="mt-1">{product.noteInterne}</p>
        </div>
      )}
      {onAddToCart && (
        <div className="pt-3 border-t border-border">
          <Button
            className="w-full gap-2"
            onClick={onAddToCart}
            data-testid="button-add-to-cart"
          >
            <ShoppingCart className="w-4 h-4" />
            Créer un devis avec ce produit
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Column auto-mapping ────────────────────────────────────────────────────
const FIELD_ALIASES: Record<string, string[]> = {
  sku:                  ["sku","reference","réference","ref","ref.","referenceproduit","codearticle","code_article","code article","partnumber","part number","article","codeproduit","code"],
  nomFr:               ["nom","nom_fr","nomfr","name","namefr","designation","désignation","libelle","libellé","produit","article_name","intitule","intitulé"],
  nomEn:               ["nom_en","nomen","nameen","name_en","english_name","nomlabel_en"],
  type:                ["type","categorie","catégorie","category","famille"],
  descriptionFr:       ["description","description_fr","descriptionfr","desc","details","détails","detail"],
  descriptionEn:       ["description_en","descriptionen","desc_en","details_en"],
  prixHt:              ["prix","prix_ht","prixht","price","tarif","tarif_ht","prixpublic","prix_public","pvp","pu"],
  prixDistribution:    ["prix_distribution","prixdistribution","distribution","dist","tarif_distribution"],
  prixRevendeurPremium:["prix_revendeur_premium","revendeurpremium","rev_premium","premium","prix_premium","tarif_premium"],
  prixRevendeur:       ["prix_revendeur","prixrevendeur","revendeur","tarif_revendeur"],
  prixClientFinal:     ["prix_client_final","prixclientfinal","client_final","clientfinal","prix_client","prixclient","tarif_client"],
  poidsNet:            ["poids_net","poidsnet","poids","weight","poids_net_kg","poids_net_g","net_weight"],
  poidsBrut:           ["poids_brut","poidsbrut","gross_weight","weight_gross","poids_brut_kg","poids_brut_g"],
  longueur:            ["longueur","length","long","longueur_mm","length_mm"],
  largeur:             ["largeur","width","larg","largeur_mm","width_mm"],
  robotsCompatibles:   ["robots_compatibles","robotscompatibles","robots","modeles","modèles","compatibles","robot_models","modeles_compatibles"],
  garantie:            ["garantie","warranty","under_warranty"],
  favori:              ["favori","favoris","favorite","starred","principauxaccessoires","principaux accessoires"],
  actif:               ["actif","active","enabled","disponible","dispo"],
  noteInterne:         ["note_interne","noteinterne","note","remarque","commentaire","internal_note"],
};

function normalizeKey(s: string) {
  return String(s).toLowerCase().replace(/[\s_\-\.]/g, "").trim();
}

function autoMapColumns(headers: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  const used = new Set<string>();
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    for (const header of headers) {
      const norm = normalizeKey(header);
      if (aliases.some(a => normalizeKey(a) === norm) && !used.has(header)) {
        mapping[field] = header;
        used.add(header);
        break;
      }
    }
  }
  return mapping;
}

function cellToString(v: any): string {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function parseBoolean(v: any): boolean {
  if (typeof v === "boolean") return v;
  const s = cellToString(v).toLowerCase();
  return ["oui","yes","true","1","x","✓","vrai"].includes(s);
}

function rowToProduct(row: Record<string, any>, mapping: Record<string, string>): InsertProduct {
  const g = (field: string) => mapping[field] ? row[mapping[field]] : undefined;
  const str = (field: string) => { const v = g(field); return v !== undefined && v !== "" ? cellToString(v) : null; };
  const num = (field: string) => { const v = g(field); if (v === undefined || v === "") return null; const n = parseFloat(String(v).replace(",",".")); return isNaN(n) ? null : String(n); };
  const robots = (): string[] | null => {
    const v = g("robotsCompatibles");
    if (!v) return null;
    const raw = cellToString(v).trim();
    if (!raw) return null;
    const found = parseRobotsCompatibles(raw);
    return found.length > 0 ? found : null;
  };
  const typeVal = str("type");
  const validTypes = ["piece_detachee","consommable","machine","accessoire","iot"];
  const typeNorm = typeVal ? typeVal.toLowerCase().replace(/[\s\-]/g,"_") : null;
  const typeMap: Record<string,string> = { "piece":"piece_detachee","pièce":"piece_detachee","piecesdetachees":"piece_detachee","piècedétachée":"piece_detachee","consommable":"consommable","machine":"machine","accessoire":"accessoire","accessory":"accessoire","iot":"iot","internet_of_things":"iot" };
  const resolvedType = typeNorm ? (validTypes.includes(typeNorm) ? typeNorm : typeMap[typeNorm] || "consommable") : "consommable";
  // Index-based fields take priority over name-based mapping
  const syntheticInt = (synKey: string, nameField: string): number | null => {
    const rawSyn = row[synKey];
    if (rawSyn !== undefined && rawSyn !== null && rawSyn !== "") {
      const n = parseFloat(String(rawSyn));
      return isNaN(n) ? null : Math.round(n);
    }
    const v = g(nameField);
    if (!v && v !== 0) return null;
    const n = parseFloat(String(v));
    return isNaN(n) ? null : Math.round(n);
  };
  const favoriRaw = g("favori");
  const isFavori = favoriRaw !== undefined
    ? parseBoolean(favoriRaw)
    : false;
  return {
    nomFr:               str("nomFr") || "",
    nomEn:               str("nomEn"),
    sku:                 str("sku") || "",
    type:                resolvedType as any,
    descriptionFr:       str("descriptionFr"),
    descriptionEn:       str("descriptionEn"),
    prixHt:              num("prixHt"),
    prixDistribution:    num("prixDistribution"),
    prixRevendeurPremium:num("prixRevendeurPremium"),
    prixRevendeur:       num("prixRevendeur"),
    prixClientFinal:     num("prixClientFinal"),
    poidsNet:            syntheticInt("__poidsNet",  "poidsNet"),
    poidsBrut:           syntheticInt("__poidsBrut", "poidsBrut"),
    longueur:            syntheticInt("__longueur",  "longueur"),
    largeur:             syntheticInt("__largeur",   "largeur"),
    robotsCompatibles:   robots(),
    garantie:            mapping["garantie"] ? parseBoolean(g("garantie")) : false,
    favori:              isFavori,
    actif:               mapping["actif"] ? parseBoolean(g("actif")) : true,
    noteInterne:         str("noteInterne"),
    imageUrl:            null,
  };
}

const FIELD_LABELS: Record<string, string> = {
  sku: "SKU", nomFr: "Nom FR", nomEn: "Nom EN", type: "Type",
  descriptionFr: "Description FR", descriptionEn: "Description EN",
  prixHt: "Prix HT", prixDistribution: "Prix distribution",
  prixRevendeurPremium: "Prix rev. premium", prixRevendeur: "Prix revendeur",
  prixClientFinal: "Prix client final", poidsNet: "Poids net (g)",
  poidsBrut: "Poids brut (g)", longueur: "Longueur (mm)", largeur: "Largeur (mm)",
  robotsCompatibles: "Robots compatibles",
  garantie: "Garantie", favori: "Favori", actif: "Actif", noteInterne: "Note interne",
};

interface ImageParseResult {
  map: Map<string, Blob>;           // key: "sheetIdx:rowInSheet" (both 0-based)
  sheetCounts: Map<number, number>; // sheetIdx → number of images found
  imagesInMedia: number;            // total blobs found in xl/media/
  rowMatchCount: number;            // total image↔row matches across all sheets
}

async function parseImagesFromXlsx(file: File): Promise<ImageParseResult> {
  const map = new Map<string, Blob>();
  const sheetCounts = new Map<number, number>();
  let imagesInMedia = 0;
  try {
    const JSZip = (await import("jszip")).default;
    const zip = await JSZip.loadAsync(await file.arrayBuffer());

    // Count all blobs in xl/media/ for diagnostic
    imagesInMedia = Object.keys(zip.files).filter(n => /^xl\/media\//i.test(n) && !zip.files[n].dir).length;

    // Iterate sheet files in order (sheet1.xml=idx0, sheet2.xml=idx1, …)
    // and look up their drawing via the sheet-level .rels file.
    const sheetPaths = Object.keys(zip.files)
      .filter(n => /xl\/worksheets\/sheet\d+\.xml$/.test(n))
      .sort((a, b) => {
        const na = parseInt(a.match(/sheet(\d+)\.xml$/)?.[1] ?? "0", 10);
        const nb = parseInt(b.match(/sheet(\d+)\.xml$/)?.[1] ?? "0", 10);
        return na - nb;
      });

    for (const sheetPath of sheetPaths) {
      const numMatch = sheetPath.match(/sheet(\d+)\.xml$/);
      if (!numMatch) continue;
      const sheetIdx = parseInt(numMatch[1], 10) - 1; // 0-based

      // Read the sheet's relationship file to find its drawing
      const sheetRelsPath = sheetPath
        .replace("xl/worksheets/", "xl/worksheets/_rels/") + ".rels";
      const sheetRelsFile = zip.files[sheetRelsPath];
      if (!sheetRelsFile) continue;
      const sheetRelsXml = await sheetRelsFile.async("text");

      const drawingRelMatch = sheetRelsXml.match(
        /Type="[^"]*drawing[^"]*"[^>]+Target="([^"]+)"/
      );
      if (!drawingRelMatch) continue;
      const drawingPath = drawingRelMatch[1].replace(/^\.\.\//, "xl/");
      const drawingFile = zip.files[drawingPath];
      if (!drawingFile) continue;

      const drawingXml = await drawingFile.async("text");
      const drawingRelsPath = drawingPath
        .replace("xl/drawings/", "xl/drawings/_rels/") + ".rels";
      const drawingRelsFile = zip.files[drawingRelsPath];
      if (!drawingRelsFile) continue;
      const drawingRelsXml = await drawingRelsFile.async("text");

      const rIdToPath: Record<string, string> = {};
      for (const m of drawingRelsXml.matchAll(/Id="(rId\d+)"[^>]+Target="([^"]+)"/g)) {
        const target = m[2].replace(/^\.\.\//, "xl/");
        rIdToPath[m[1]] = target;
      }

      let countForSheet = 0;
      const anchorRx = /<xdr:(?:one|two)CellAnchor[\s\S]*?<\/xdr:(?:one|two)CellAnchor>/g;
      for (const [anchorBlock] of drawingXml.matchAll(anchorRx)) {
        const rowM = anchorBlock.match(/<xdr:from>[\s\S]*?<xdr:row>(\d+)<\/xdr:row>/);
        const embedM = anchorBlock.match(/r:embed="(rId\d+)"/);
        if (!rowM || !embedM) continue;
        // xdr:row is 0-based; row 0 = header → skip; row 1 = jsonRows[0]
        const xdrRow = parseInt(rowM[1], 10);
        const rowInSheet = xdrRow - 1; // 0-based index into jsonRows
        if (rowInSheet < 0) continue;
        const imagePath = rIdToPath[embedM[1]];
        if (!imagePath) continue;
        const imageFile = zip.files[imagePath];
        if (!imageFile) continue;
        // Derive MIME type from the path extension (JSZip blobs have no type by default)
        const extRaw = imagePath.split(".").pop()?.toLowerCase() ?? "png";
        const mimeType = extRaw === "jpg" || extRaw === "jpeg" ? "image/jpeg"
          : extRaw === "png" ? "image/png"
          : extRaw === "gif" ? "image/gif"
          : extRaw === "webp" ? "image/webp"
          : "image/png";
        const rawArr = await imageFile.async("uint8array");
        const blob = new Blob([rawArr], { type: mimeType });
        map.set(`${sheetIdx}:${rowInSheet}`, blob);
        countForSheet++;
      }
      sheetCounts.set(sheetIdx, countForSheet);
    }
  } catch {}
  return { map, sheetCounts, imagesInMedia, rowMatchCount: map.size };
}

type ImportStep = "drop" | "preview" | "importing" | "done";

function ImportDialog({
  onDone,
}: {
  onDone: (ok: number, err: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<ImportStep>("drop");
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [parsed, setParsed] = useState<InsertProduct[]>([]);
  const [progress, setProgress] = useState({ done: 0, total: 0, ok: 0, err: 0 });
  const [importPhase, setImportPhase] = useState<"images" | "sending">("images");
  const [errorLog, setErrorLog] = useState<{ sheetIdx: number; rowInSheet: number; sku: string; errorMsg: string }[]>([]);
  const [imageMap, setImageMap] = useState<Map<string, Blob>>(new Map());
  const [imgSheetCounts, setImgSheetCounts] = useState<Map<number, number>>(new Map());
  const [imagesParsing, setImagesParsing] = useState(false);
  const [sheetBreakdown, setSheetBreakdown] = useState<{ name: string; rowCount: number; skippedCount: number; structureWarning?: string; detectedCols: string[] }[]>([]);
  const [dupCount, setDupCount] = useState(0);
  const [uploadedImgCount, setUploadedImgCount] = useState(0);
  const [withImageCount, setWithImageCount] = useState(0);
  const [imgStats, setImgStats] = useState({ inZip: 0, rowMatches: 0, attempts: 0, successes: 0, withUrl: 0 });
  const [firstUploadError, setFirstUploadError] = useState<string | null>(null);
  const [chunkLog, setChunkLog] = useState<{ lot: number; count: number; status: "ok" | "error"; error?: string }[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep("drop"); setIsDragging(false); setFileName("");
    setHeaders([]); setMapping({}); setRows([]); setParsed([]);
    setProgress({ done: 0, total: 0, ok: 0, err: 0 });
    setImportPhase("images");
    setErrorLog([]);
    setImageMap(new Map()); setImgSheetCounts(new Map()); setImagesParsing(false);
    setSheetBreakdown([]);
    setDupCount(0);
    setUploadedImgCount(0);
    setWithImageCount(0);
    setImgStats({ inZip: 0, rowMatches: 0, attempts: 0, successes: 0, withUrl: 0 });
    setFirstUploadError(null);
    setChunkLog([]);
  };

  const parseFile = useCallback((file: File) => {
    if (!file.name.match(/\.(xlsx?|xls)$/i)) return;
    setFileName(file.name);
    setImagesParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });

        let allRows: Record<string, any>[] = [];
        let allParsed: InsertProduct[] = [];
        const breakdown: { name: string; rowCount: number; skippedCount: number; structureWarning?: string; detectedCols: string[] }[] = [];
        let refMapping: Record<string, string> = {};
        let refHeaders: string[] = [];
        let isFirst = true;

        // Fixed column indices (0-based): J=9, K=10, L=11, M=12
        const IDX_POIDS_NET  = 9;
        const IDX_POIDS_BRUT = 10;
        const IDX_LONGUEUR   = 11;
        const IDX_LARGEUR    = 12;
        const colIndexDefs: { key: string; idx: number; label: string }[] = [
          { key: "__poidsNet",  idx: IDX_POIDS_NET,  label: "Poids net (J)" },
          { key: "__poidsBrut", idx: IDX_POIDS_BRUT, label: "Poids brut (K)" },
          { key: "__longueur",  idx: IDX_LONGUEUR,   label: "Longueur (L)" },
          { key: "__largeur",   idx: IDX_LARGEUR,    label: "Largeur (M)" },
        ];

        // Use entries() so sheetIdx is always correct even when we `continue`
        for (const [sheetIdx, sheetName] of wb.SheetNames.entries()) {
          const ws = wb.Sheets[sheetName];
          let jsonRows: Record<string, any>[] = [];
          let rawRows: any[][] = [];
          try {
            jsonRows = XLSX.utils.sheet_to_json(ws, { defval: "" });
            rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null }) as any[][];
          } catch {
            breakdown.push({ name: sheetName, rowCount: 0, skippedCount: 0, structureWarning: "Impossible de lire cette feuille", detectedCols: [] });
            continue;
          }
          if (jsonRows.length === 0) {
            breakdown.push({ name: sheetName, rowCount: 0, skippedCount: 0, detectedCols: [] });
            continue;
          }

          // Enrich each row with index-based values injected as synthetic keys
          // rawRows[0] = header row, rawRows[i+1] = jsonRows[i]
          const enrichedRows = jsonRows.map((row, i) => {
            const raw: any[] = rawRows[i + 1] ?? [];
            const clone = { ...row } as Record<string, any>;
            // Sheet + row position for image lookup
            clone.__sheetIdx  = sheetIdx;
            clone.__rowInSheet = i; // 0-based index into jsonRows (= drawing xdrRow - 1)
            for (const { key, idx } of colIndexDefs) {
              const v = raw[idx];
              if (v !== null && v !== undefined && v !== "" && !isNaN(Number(v))) {
                clone[key] = Number(v);
              }
            }
            return clone;
          });

          // Detect which index-based columns have at least one numeric value in this sheet
          const detectedCols: string[] = colIndexDefs
            .filter(({ key }) => enrichedRows.some(r => r[key] !== undefined))
            .map(({ label }) => label);

          const hdrs = Object.keys(jsonRows[0]);
          const mp = autoMapColumns(hdrs);

          let structureWarning: string | undefined;
          if (isFirst) {
            refHeaders = hdrs;
            refMapping = mp;
            isFirst = false;
          } else {
            const refFields = Object.keys(refMapping);
            const curFields = Object.keys(mp);
            const missing = refFields.filter(f => !curFields.includes(f));
            const extra = curFields.filter(f => !refFields.includes(f));
            if (missing.length > 0 || extra.length > 0) {
              const parts: string[] = [];
              if (missing.length > 0) parts.push(`manquants: ${missing.join(", ")}`);
              if (extra.length > 0) parts.push(`nouveaux: ${extra.join(", ")}`);
              structureWarning = parts.join(" · ");
            }
          }

          const skuCol = mp["sku"];
          const validRows = enrichedRows.filter(r => {
            if (!skuCol) return false;
            const sku = String(r[skuCol] ?? "").trim();
            return sku !== "" && !/^(sku|référence|ref|code|article|n°)$/i.test(sku);
          });
          const skippedCount = enrichedRows.length - validRows.length;
          const rawParsed = validRows.map(r => ({ raw: r, product: rowToProduct(r, mp) }));
          const withSku = rawParsed.filter(({ product: p }) => p.sku && p.sku.trim() !== "");

          allRows = [...allRows, ...withSku.map(x => x.raw)];
          allParsed = [...allParsed, ...withSku.map(x => x.product)];
          const totalSkipped = enrichedRows.length - withSku.length;
          breakdown.push({ name: sheetName, rowCount: withSku.length, skippedCount: totalSkipped, structureWarning, detectedCols });
        }

        if (allRows.length === 0) return;
        setHeaders(refHeaders);
        // Dédupliquer par SKU — conserver la DERNIÈRE occurrence (feuille la plus récente)
        const skuMap = new Map<string, { raw: Record<string, any>; product: InsertProduct }>();
        for (let i = 0; i < allParsed.length; i++) {
          skuMap.set(allParsed[i].sku, { raw: allRows[i], product: allParsed[i] });
        }
        const dedupedParsed = Array.from(skuMap.values()).map(x => x.product);
        const dedupedRows   = Array.from(skuMap.values()).map(x => x.raw);
        const dupDetected = allParsed.length - dedupedParsed.length;

        setMapping(refMapping);
        setRows(dedupedRows);
        setParsed(dedupedParsed);
        setDupCount(dupDetected);
        setSheetBreakdown(breakdown);
        setStep("preview");
      } catch {}
    };
    reader.readAsArrayBuffer(file);
    parseImagesFromXlsx(file).then(({ map, sheetCounts, imagesInMedia, rowMatchCount }) => {
      setImageMap(map);
      setImgSheetCounts(sheetCounts);
      setImgStats(s => ({ ...s, inZip: imagesInMedia, rowMatches: rowMatchCount }));
      setImagesParsing(false);
    }).catch(() => setImagesParsing(false));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }, [parseFile]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const handleImport = async () => {
    setStep("importing");
    setImportPhase("images");

    // Phase 1 : upload des images via backend (/api/products/upload-image → admin key bypass RLS)
    const finalRows: InsertProduct[] = [];
    let uploadedCount = 0;
    let attemptCount = 0;
    let firstUploadError: string | null = null;
    for (let i = 0; i < parsed.length; i++) {
      let row = parsed[i];
      const imgKey = `${rows[i].__sheetIdx}:${rows[i].__rowInSheet}`;
      const imgBlob = imageMap.get(imgKey);
      if (imgBlob && !row.imageUrl) {
        attemptCount++;
        setImgStats(s => ({ ...s, attempts: attemptCount }));
        try {
          const formData = new FormData();
          formData.append("file", imgBlob, `img_${i}.${imgBlob.type.split("/")[1] || "jpg"}`);
          const upRes = await authFetch("/api/products/upload-image", { method: "POST", body: formData });
          if (upRes.ok) {
            const { url } = await upRes.json();
            row = { ...row, imageUrl: url };
            uploadedCount++;
            setImgStats(s => ({ ...s, successes: uploadedCount }));
          } else {
            const errBody = await upRes.json().catch(() => ({}));
            if (!firstUploadError) {
              firstUploadError = `HTTP ${upRes.status}: ${errBody.message || "Erreur inconnue"}`;
              console.error("[upload-image] Premier échec:", firstUploadError);
            }
          }
        } catch (e: any) {
          if (!firstUploadError) {
            firstUploadError = `Exception: ${e?.message || "Erreur réseau"}`;
            console.error("[upload-image] Exception:", firstUploadError);
          }
        }
      }
      finalRows.push(row);
      setProgress({ done: i + 1, total: parsed.length, ok: i + 1, err: 0 });
    }
    const withUrlCount = finalRows.filter(r => !!r.imageUrl).length;
    setUploadedImgCount(uploadedCount);
    setWithImageCount(withUrlCount);
    setImgStats(s => ({ ...s, attempts: attemptCount, successes: uploadedCount, withUrl: withUrlCount }));
    if (firstUploadError) setFirstUploadError(firstUploadError);

    // Phase 2 : envoi en lots de 100 — DELETE sur le premier lot, INSERT seuls sur les suivants
    setImportPhase("sending");
    const CHUNK = 100;
    const chunks: InsertProduct[][] = [];
    for (let i = 0; i < finalRows.length; i += CHUNK) chunks.push(finalRows.slice(i, i + CHUNK));
    setProgress({ done: 0, total: chunks.length, ok: 0, err: 0 });
    setChunkLog([]);

    let failed = false;
    for (let ci = 0; ci < chunks.length; ci++) {
      try {
        const res = await authFetch("/api/products/import", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rows: chunks[ci], deleteFirst: ci === 0 }),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          const errMsg = errBody.message || `HTTP ${res.status}`;
          setChunkLog(prev => [...prev, { lot: ci + 1, count: chunks[ci].length, status: "error", error: errMsg }]);
          setErrorLog([{ sheetIdx: 0, rowInSheet: ci * CHUNK, sku: "—", errorMsg: `Lot ${ci + 1}: ${errMsg}` }]);
          failed = true;
          setProgress({ done: ci + 1, total: chunks.length, ok: ci, err: chunks.length - ci });
          break;
        }
        setChunkLog(prev => [...prev, { lot: ci + 1, count: chunks[ci].length, status: "ok" }]);
        setProgress({ done: ci + 1, total: chunks.length, ok: ci + 1, err: 0 });
      } catch (err: any) {
        const errMsg = err?.message || "Erreur réseau";
        setChunkLog(prev => [...prev, { lot: ci + 1, count: chunks[ci].length, status: "error", error: errMsg }]);
        setErrorLog([{ sheetIdx: 0, rowInSheet: ci * CHUNK, sku: "—", errorMsg: `Lot ${ci + 1}: ${errMsg}` }]);
        failed = true;
        setProgress({ done: ci + 1, total: chunks.length, ok: ci, err: chunks.length - ci });
        break;
      }
    }

    if (!failed) {
      setErrorLog([]);
      onDone(finalRows.length, 0);
    }
    setStep("done");
  };

  const mappedCount = Object.keys(mapping).length;
  const requiredMapped = mapping["sku"] && mapping["nomFr"];
  const previewRows = rows.slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="button-import-products">
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Importer XLS
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[780px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import catalogue Excel</DialogTitle>
          <DialogDescription>
            {step === "drop" && "Glissez votre fichier .xlsx ou .xls dans la zone ci-dessous."}
            {step === "preview" && `${sheetBreakdown.length} feuille(s) · ${rows.length} produit(s) au total dans « ${fileName} »`}
            {step === "importing" && `Import en cours… ${progress.done} / ${progress.total}`}
            {step === "done" && "Import terminé."}
          </DialogDescription>
        </DialogHeader>

        {step === "drop" && (
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className={`mt-2 flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed cursor-pointer transition-all py-16 px-8
              ${isDragging ? "border-primary bg-primary/5 scale-[1.01]" : "border-muted-foreground/30 hover:border-primary/50 hover:bg-secondary/30"}`}
            data-testid="dropzone-import"
          >
            <FileSpreadsheet className={`w-14 h-14 transition-colors ${isDragging ? "text-primary" : "text-muted-foreground/50"}`} />
            <div className="text-center">
              <p className="font-semibold text-foreground">Glissez votre fichier ici</p>
              <p className="text-sm text-muted-foreground mt-1">ou cliquez pour sélectionner un fichier .xlsx / .xls</p>
            </div>
            <Badge variant="outline" className="text-xs">Formats acceptés : .xlsx, .xls</Badge>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileInput} data-testid="input-file-import" />
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4 mt-2">
            {/* Sheet breakdown */}
            {sheetBreakdown.length > 0 && (
              <div className="rounded-xl border bg-secondary/20 p-4 space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold">Feuilles détectées ({sheetBreakdown.length})</p>
                  <Badge variant="outline" className="text-xs font-semibold">{rows.length} produits au total</Badge>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                  {sheetBreakdown.map((sheet, i) => (
                    <div key={i} className={`text-xs rounded-lg px-3 py-2 border space-y-1 ${sheet.structureWarning ? "bg-amber-50/60 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800" : sheet.rowCount === 0 ? "bg-secondary/30 border-transparent opacity-60" : "bg-card border-border"}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          {sheet.structureWarning
                            ? <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                            : sheet.rowCount > 0
                              ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                              : <AlertCircle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                          <span className="font-medium truncate">{sheet.name}</span>
                          {sheet.structureWarning && (
                            <span className="text-amber-600 dark:text-amber-400 truncate hidden sm:inline">— {sheet.structureWarning}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <span className={`font-semibold ${sheet.rowCount > 0 ? "text-foreground" : "text-muted-foreground"}`}>{sheet.rowCount}</span>
                          <span className="text-muted-foreground">ligne{sheet.rowCount !== 1 ? "s" : ""}</span>
                          {sheet.skippedCount > 0 && (
                            <span className="text-muted-foreground/60">({sheet.skippedCount} ignorée{sheet.skippedCount !== 1 ? "s" : ""})</span>
                          )}
                        </div>
                      </div>
                      {sheet.rowCount > 0 && (
                        <div className="flex flex-wrap gap-1 pt-0.5">
                          {(["Poids net (J)", "Poids brut (K)", "Longueur (L)", "Largeur (M)"] as const).map(label => {
                            const active = sheet.detectedCols.includes(label);
                            return (
                              <span key={label} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${active ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400" : "bg-secondary/40 border-border text-muted-foreground/50 line-through"}`}>
                                {label}
                              </span>
                            );
                          })}
                          {(() => {
                            const imgCount = imgSheetCounts.get(i);
                            if (!imagesParsing && imgCount !== undefined) {
                              return (
                                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border ${imgCount > 0 ? "bg-blue-50 dark:bg-blue-950/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400" : "bg-secondary/40 border-border text-muted-foreground/50"}`}>
                                  🖼 {imgCount} img
                                </span>
                              );
                            }
                            if (imagesParsing) {
                              return (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border bg-secondary/40 border-border text-muted-foreground/50">
                                  🖼 …
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mapping summary */}
            <div className="rounded-xl border bg-secondary/20 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Colonnes détectées automatiquement</p>
                <Badge variant="outline" className="text-xs">{mappedCount} / {Object.keys(FIELD_LABELS).length} champs reconnus</Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(FIELD_LABELS).map(([field, label]) => {
                  const col = mapping[field];
                  return (
                    <div key={field} className={`flex items-center gap-2 text-xs rounded-lg px-2 py-1.5 border ${col ? "bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800" : "bg-secondary/30 border-transparent"}`}>
                      {col
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        : <AlertCircle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                      <span className={col ? "text-foreground" : "text-muted-foreground/50"}>{label}</span>
                      {col && <span className="ml-auto font-mono text-[10px] text-muted-foreground truncate max-w-[80px]">← {col}</span>}
                    </div>
                  );
                })}
              </div>
              {!requiredMapped && (
                <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  Les colonnes <strong>SKU</strong> et <strong>Nom FR</strong> sont requises. Vérifiez les en-têtes de votre fichier.
                </div>
              )}
            </div>

            {/* Data preview */}
            <div className="space-y-2">
              <p className="text-sm font-semibold">Aperçu des 5 premières lignes</p>
              <div className="overflow-x-auto rounded-xl border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                      <TableHead className="text-xs py-2">SKU</TableHead>
                      <TableHead className="text-xs py-2">Nom FR</TableHead>
                      <TableHead className="text-xs py-2">Type</TableHead>
                      <TableHead className="text-xs py-2">Prix HT</TableHead>
                      <TableHead className="text-xs py-2">Robots</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((r, i) => {
                      const p = parsed[i];
                      return (
                        <TableRow key={i} className="text-xs">
                          <TableCell className="py-1.5 font-mono">{p?.sku || <span className="text-red-400">—</span>}</TableCell>
                          <TableCell className="py-1.5 max-w-[180px] truncate">{p?.nomFr || <span className="text-red-400">—</span>}</TableCell>
                          <TableCell className="py-1.5">{p?.type || "—"}</TableCell>
                          <TableCell className="py-1.5">{p?.prixHt ? `${p.prixHt} €` : "—"}</TableCell>
                          <TableCell className="py-1.5">
                            <div className="flex flex-wrap gap-0.5">
                              {p?.robotsCompatibles?.slice(0,3).map((rob: string, ri: number) => (
                                <Badge key={ri} variant="outline" className="text-[10px] px-1 py-0">{rob}</Badge>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {rows.length > 5 && (
                <p className="text-xs text-muted-foreground text-center">… et {rows.length - 5} autre(s) ligne(s)</p>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={reset}>
                  <X className="w-4 h-4 mr-1" /> Changer de fichier
                </Button>
                {imagesParsing && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                    Extraction images…
                  </span>
                )}
                {!imagesParsing && imageMap.size > 0 && (
                  <Badge className="text-xs bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800">
                    🖼 {imageMap.size} image(s) détectée(s)
                  </Badge>
                )}
              </div>
              <Button
                onClick={handleImport}
                disabled={!requiredMapped || imagesParsing}
                data-testid="button-confirm-import"
                className="shadow-lg shadow-primary/20"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importer {rows.length} produit{rows.length > 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        )}

        {step === "importing" && (
          <div className="py-8 flex flex-col items-center gap-5">
            <div className="w-14 h-14 rounded-full border-4 border-primary border-t-transparent animate-spin" />

            {importPhase === "images" ? (
              <>
                <p className="font-semibold">Phase 1 — Préparation des images…</p>
                <div className="w-full max-w-xs bg-secondary/40 rounded-full h-2 overflow-hidden">
                  <div className="h-2 bg-primary rounded-full transition-all duration-150"
                    style={{ width: `${parsed.length > 0 ? (progress.done / parsed.length) * 100 : 0}%` }} />
                </div>
                <p className="text-sm text-muted-foreground">{progress.done} / {parsed.length} produits traités</p>
                {/* 5 compteurs diagnostics live */}
                <div className="w-full max-w-sm rounded-xl border border-border bg-secondary/20 overflow-hidden text-xs">
                  {[
                    { label: "Images dans le ZIP (xl/media/)", val: imgStats.inZip },
                    { label: "Correspondances image↔ligne", val: imgStats.rowMatches },
                    { label: "Uploads tentés", val: imgStats.attempts },
                    { label: "Uploads réussis", val: imgStats.successes },
                    { label: "Produits avec image_url", val: imgStats.withUrl },
                  ].map(({ label, val }, idx, arr) => (
                    <div key={label} className={`flex items-center justify-between px-3 py-2 ${idx < arr.length - 1 ? "border-b border-border/50" : ""}`}>
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-mono font-semibold tabular-nums">{val}</span>
                    </div>
                  ))}
                </div>

                {/* Erreur upload (premier échec) */}
                {firstUploadError && (
                  <div className="w-full max-w-sm rounded-xl border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                    <span className="font-semibold">Premier échec upload : </span>{firstUploadError}
                  </div>
                )}

                {/* Récap par feuille */}
                {sheetBreakdown.length > 0 && (
                  <div className="w-full max-w-sm rounded-xl border border-border overflow-hidden text-xs">
                    <div className="bg-secondary/40 border-b border-border px-3 py-1.5 font-semibold text-muted-foreground">
                      Lignes parsées par feuille
                    </div>
                    {sheetBreakdown.map((s, i) => (
                      <div key={i} className={`flex items-center justify-between px-3 py-1.5 ${i < sheetBreakdown.length - 1 ? "border-b border-border/50" : ""}`}>
                        <span className="text-muted-foreground truncate max-w-[200px]" title={s.name}>F{i + 1} · {s.name}</span>
                        <span className="font-mono font-semibold tabular-nums ml-2">{s.rowCount} <span className="font-normal text-muted-foreground">({s.skippedCount} ignorées)</span></span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-1.5 bg-secondary/20 border-t border-border font-semibold">
                      <span>Total</span>
                      <span className="font-mono tabular-nums">{sheetBreakdown.reduce((a, s) => a + s.rowCount, 0)}</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="font-semibold">Phase 2 — Envoi en base…</p>
                <div className="w-full max-w-xs bg-secondary/40 rounded-full h-2 overflow-hidden">
                  <div className="h-2 bg-primary rounded-full transition-all duration-150"
                    style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }} />
                </div>
                {chunkLog.length > 0 && (
                  <div className="w-full max-w-sm rounded-xl border border-border overflow-hidden text-xs">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-secondary/40 border-b border-border">
                          <th className="text-left px-3 py-1.5 font-semibold text-muted-foreground">Lot</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-muted-foreground">Produits</th>
                          <th className="text-left px-3 py-1.5 font-semibold text-muted-foreground">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chunkLog.map(c => (
                          <tr key={c.lot} className="border-b border-border/50 last:border-0">
                            <td className="px-3 py-1.5 font-mono">#{c.lot}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{c.count}</td>
                            <td className="px-3 py-1.5">
                              {c.status === "ok"
                                ? <span className="text-emerald-600">✓ OK</span>
                                : <span className="text-red-500 truncate block max-w-[160px]" title={c.error}>✗ {c.error}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
            <p className="text-xs text-muted-foreground">Ne fermez pas cette fenêtre pendant l'import.</p>
          </div>
        )}

        {step === "done" && (
          <div className="py-6 flex flex-col gap-5">
            {/* Header */}
            <div className="flex flex-col items-center gap-2">
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
              <p className="font-semibold text-lg">Import terminé</p>
              <div className="flex items-center gap-3 text-sm flex-wrap justify-center">
                <span className="text-emerald-600 font-medium">{progress.ok} importé{progress.ok !== 1 ? "s" : ""}</span>
                {dupCount > 0 && <span className="text-amber-600 font-medium">{dupCount} doublon{dupCount !== 1 ? "s" : ""} fusionné{dupCount !== 1 ? "s" : ""}</span>}
                {progress.err > 0 && <span className="text-red-500 font-medium">{progress.err} erreur{progress.err !== 1 ? "s" : ""}</span>}
              </div>
              {(uploadedImgCount > 0 || withImageCount > 0) && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground border rounded-lg px-4 py-2 bg-secondary/30 mt-1">
                  <span><span className="font-semibold text-foreground">{uploadedImgCount}</span> image{uploadedImgCount !== 1 ? "s" : ""} uploadée{uploadedImgCount !== 1 ? "s" : ""}</span>
                  <span className="text-border">·</span>
                  <span><span className={`font-semibold ${withImageCount === uploadedImgCount ? "text-emerald-600" : "text-amber-600"}`}>{withImageCount}</span> produit{withImageCount !== 1 ? "s" : ""} avec image_url renseignée</span>
                </div>
              )}
            </div>

            {/* Error details */}
            {errorLog.length > 0 && (() => {
              // Group errors by message
              const groups = errorLog.reduce<Record<string, number>>((acc, e) => {
                acc[e.errorMsg] = (acc[e.errorMsg] ?? 0) + 1;
                return acc;
              }, {});
              const sortedGroups = Object.entries(groups).sort((a, b) => b[1] - a[1]);
              const first10 = errorLog.slice(0, 10);

              return (
                <div className="space-y-4 w-full">
                  {/* First 10 errors detail */}
                  <div className="space-y-2">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      {first10.length < errorLog.length
                        ? `10 premières erreurs (sur ${errorLog.length})`
                        : `Détail des ${errorLog.length} erreur${errorLog.length !== 1 ? "s" : ""}`}
                    </p>
                    <div className="rounded-xl border border-red-100 dark:border-red-900/40 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-red-50/60 dark:bg-red-950/20 border-b border-red-100 dark:border-red-900/40">
                            <th className="text-left px-3 py-2 font-semibold text-muted-foreground w-20">Feuille</th>
                            <th className="text-left px-3 py-2 font-semibold text-muted-foreground w-16">Ligne</th>
                            <th className="text-left px-3 py-2 font-semibold text-muted-foreground w-32">SKU</th>
                            <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Message d'erreur</th>
                          </tr>
                        </thead>
                        <tbody>
                          {first10.map((e, idx) => (
                            <tr key={idx} className={`border-b border-red-50 dark:border-red-900/20 last:border-0 ${idx % 2 === 0 ? "bg-card" : "bg-red-50/20 dark:bg-red-950/10"}`}>
                              <td className="px-3 py-2 font-medium">F{e.sheetIdx + 1}</td>
                              <td className="px-3 py-2 text-muted-foreground">L{e.rowInSheet + 2}</td>
                              <td className="px-3 py-2 font-mono truncate max-w-[120px]" title={e.sku}>{e.sku}</td>
                              <td className="px-3 py-2 text-red-600 dark:text-red-400 break-all">{e.errorMsg}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Grouped summary */}
                  <div className="space-y-2">
                    <p className="text-sm font-semibold">Résumé par type d'erreur ({sortedGroups.length} type{sortedGroups.length !== 1 ? "s" : ""})</p>
                    <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                      {sortedGroups.map(([msg, count], idx) => (
                        <div key={idx} className="flex items-start gap-2 rounded-lg border border-red-100 dark:border-red-900/30 bg-red-50/30 dark:bg-red-950/10 px-3 py-2">
                          <Badge variant="outline" className="shrink-0 text-red-600 border-red-300 dark:border-red-700 dark:text-red-400 font-semibold text-[10px] mt-0.5">
                            ×{count}
                          </Badge>
                          <span className="text-xs text-red-700 dark:text-red-300 break-all">{msg}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Diagnostics images */}
            {(imgStats.inZip > 0 || imgStats.rowMatches > 0 || imgStats.attempts > 0) && (
              <div className="space-y-2">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  Diagnostic images
                </p>
                <div className="rounded-xl border border-border overflow-hidden text-xs">
                  {[
                    { label: "Images dans le ZIP (xl/media/)", val: imgStats.inZip },
                    { label: "Correspondances image↔ligne", val: imgStats.rowMatches },
                    { label: "Uploads tentés", val: imgStats.attempts },
                    { label: "Uploads réussis", val: imgStats.successes },
                    { label: "Produits avec image_url", val: imgStats.withUrl },
                  ].map(({ label, val }, idx, arr) => (
                    <div key={label} className={`flex items-center justify-between px-3 py-2 bg-secondary/10 ${idx < arr.length - 1 ? "border-b border-border/50" : ""}`}>
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-mono font-semibold tabular-nums">{val}</span>
                    </div>
                  ))}
                </div>
                {firstUploadError && (
                  <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                    <span className="font-semibold">Premier échec upload : </span>{firstUploadError}
                  </div>
                )}
              </div>
            )}

            {/* Journal des lots */}
            {chunkLog.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold">Journal des lots ({chunkLog.length})</p>
                <div className="rounded-xl border border-border overflow-hidden text-xs max-h-48 overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-secondary/40 border-b border-border">
                        <th className="text-left px-3 py-1.5 font-semibold text-muted-foreground">Lot</th>
                        <th className="text-right px-3 py-1.5 font-semibold text-muted-foreground">Produits</th>
                        <th className="text-left px-3 py-1.5 font-semibold text-muted-foreground">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chunkLog.map(c => (
                        <tr key={c.lot} className={`border-b border-border/50 last:border-0 ${c.status === "error" ? "bg-red-50/40 dark:bg-red-950/20" : ""}`}>
                          <td className="px-3 py-1.5 font-mono">#{c.lot}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{c.count}</td>
                          <td className="px-3 py-1.5">
                            {c.status === "ok"
                              ? <span className="text-emerald-600">✓ OK</span>
                              : <span className="text-red-500 break-all" title={c.error}>✗ {c.error}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-center pt-2">
              <Button onClick={() => { setOpen(false); reset(); }}>Fermer</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
